import {
    Form,
    FormField,
    FormSection,
    FormType,
    InputType,
    DropdownData,
    FilteredValue,
    RepeatableSubField,
    RepeatableSubFieldDependencyContext,
} from '@fitogest/reactive-form-builder/interfaces';
import { StorageManager } from '../../../../../common/storage-manager.class';
import { environment } from 'src/environments/environment';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { ParcelasModel } from 'src/app/models/form-common/parcelas.model';
import { MaquinariaModel } from 'src/app/models/trabajadores/maquinaria.model';
import { TareasBioModel } from 'src/app/models/tareas/tareas-bio.model';
import { TratamientosControlModel } from 'src/app/models/tratamientos/tratamientos-control.model';
import { TratamientosControlDropdownDataSourceFactory } from './tratamientos-control-dropdown-datasource.factory';
import { TratamientosControlValueResolver } from './tratamientos-control-value-resolver';
import { TratamientosControlProductOptionMapper } from './tratamientos-control-product-option.mapper';

const createDropdownData = <T>(): DropdownData<T> => ({ filtered: [], values: [] });

export type TratamientosControlMagramaSlotData = {
    usosFiltered: FilteredValue<string>[];
    agentesFiltered: FilteredValue<string>[];
    udsFiltered: Array<{ label: string; value: string | null }>;
    dosisFiltered: Array<{ label: string; value: string | null }>;
    agentesByUso: Record<string, FilteredValue<string>[]>;
    udsByUso: Record<string, Array<{ label: string; value: string | null }>>;
    dosisByUso: Record<string, Array<{ label: string; value: string | null }>>;
};

export type TratamientosControlMagramaLoaderContext = {
    rowIndex: number;
    slot: 1 | 2 | 3;
    productId: string;
    product: TareasBioModel | null;
    referencia: string;
};

export type TratamientosControlMagramaLoader = (
    context: TratamientosControlMagramaLoaderContext,
) => Promise<TratamientosControlMagramaSlotData | null>;


export class TratamientosControlListsDefinition {
    public fincas: DropdownData<FincasModel> = createDropdownData<FincasModel>();
    public parcelas: DropdownData<ParcelasModel> = createDropdownData<ParcelasModel>();
    public maquinarias: DropdownData<MaquinariaModel> = createDropdownData<MaquinariaModel>();
    public productos: DropdownData<TareasBioModel> = createDropdownData<TareasBioModel>();
}

export class TratamientosControlFormDefinition {
    public formType!: FormType;

    public appName = environment.appName;
    public fincaName = environment.features.fincaName;
    public parcelasName = environment.features.parcelasName;

    public lists = new TratamientosControlListsDefinition();

    public cliente = StorageManager.getClient();
    public usuario = StorageManager.getUser();

    public model: TratamientosControlModel = { estado: 'Planificado' };

    public formFields!: Form<TratamientosControlModel>;

    constructor(type: FormType) {
        this.formType = +type;
        this.formFields = this.buildForm();
    }

    private readonly dropdownDataSourceFactory = new TratamientosControlDropdownDataSourceFactory(() => this.lists.productos);

    public setMagramaDataLoader(loader: TratamientosControlMagramaLoader): void {
        this.dropdownDataSourceFactory.setMagramaDataLoader(loader);
    }


    public setFincasDataSource(endpoint: unknown): void {
        const field = this.findField('id_finca');

        if (!field) {
            return;
        }

        field.dataSource = {
            endpoint,
            cacheKey: 'tratamientos-control-fincas',
            labelKey: 'nombre',
            distinctBy: 'id',
        };
    }

    public setParcelasDataSource(endpoint: unknown): void {
        const field = this.findField('ids_parcelas');

        if (!field) {
            return;
        }

        field.dataSource = {
            endpoint,
            cacheKey: 'tratamientos-control-parcelas',
            dependsOn: ['id_finca'],
            labelKey: 'nombre',
            distinctBy: 'id',
            filterOption: (option, context) => this.isParcelaOptionForSelectedFinca(
                option,
                context.getSelectedBackendValue('id_finca') ?? context.model?.get('id_finca')?.value,
            ),
        };
    }

    public setMaquinariaDataSource(endpoint: unknown): void {
        const field = this.findField('id_maquinaria');

        if (!field) {
            return;
        }

        field.dataSource = {
            endpoint,
            cacheKey: 'tratamientos-control-maquinarias',
            labelKey: 'nombre',
            distinctBy: 'id',
        };
    }


    public setProductosDataSource(endpoint: unknown): void {
        const subField = this.findRepeatableSubField('lineas_materias', 'producto');

        if (!subField) {
            return;
        }

        subField.dataSource = {
            endpoint,
            cacheKey: 'tratamientos-control-productos',
            labelKey: 'materia_activa',
            distinctBy: 'id',
            mapOption: (item) => TratamientosControlProductOptionMapper.mapRawItem(item),
        };
    }

    public resolveProductRawValue(value: unknown): unknown | null {
        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            return value;
        }

        const productId = TratamientosControlValueResolver.resolveOptionId(value);

        if (productId === '') {
            return null;
        }

        const productOptions = this.getRepeatableSubFieldOptions('lineas_materias', 'producto');
        const found = productOptions.find((option) => this.isProductOptionMatch(option, productId));

        return found?.raw ?? found?.value ?? null;
    }

    public updateRequiredFieldsForEstado(estado: unknown): void {
        if (this.appName !== 'hernandorena') {
            return;
        }

        const isAplicado = estado === 'Aplicado';
        const requiredWhenAplicado = new Set([
            'ids_parcelas', 'ph_caldo', 'litros', 'ph_agua',
            'ph_final', 'nivel_peligrosidad', 'id_maquinaria', 'estado'
        ]);

        for (const section of this.formFields.sections) {
            for (const field of section.fields) {
                if (requiredWhenAplicado.has(field.name as string)) {
                    field.conditions.isRequired = isAplicado;
                }
            }
        }
    }

    private findField(fieldName: string): FormField<TratamientosControlModel> | null {
        for (const section of this.formFields.sections) {
            const field = section.fields.find((item) => item.name === fieldName);

            if (field) {
                return field as FormField<TratamientosControlModel>;
            }
        }

        return null;
    }


    private findRepeatableSubField(fieldName: string, subFieldKey: string): RepeatableSubField | null {
        const field = this.findField(fieldName);
        const subFields = field?.input?.repeatableGroupConfiguration?.subFields ?? [];
        const subField = subFields.find((item) => item.key === subFieldKey);

        return subField ?? null;
    }

    private getRepeatableSubFieldOptions(fieldName: string, subFieldKey: string): Array<FilteredValue<unknown>> {
        const subField = this.findRepeatableSubField(fieldName, subFieldKey);
        const data = subField?.input?.data as Partial<DropdownData<unknown>> | null | undefined;
        const values = Array.isArray(data?.values) ? data.values : [];
        const filtered = Array.isArray(data?.filtered) ? data.filtered : [];

        if (values.length > 0) {
            return values as Array<FilteredValue<unknown>>;
        }

        return filtered as Array<FilteredValue<unknown>>;
    }

    private isProductOptionMatch(option: FilteredValue<unknown>, productId: string): boolean {
        const candidates = [
            ...TratamientosControlValueResolver.getProductIdentityCandidates(option.value),
            ...TratamientosControlValueResolver.getProductIdentityCandidates(option.raw),
        ];

        return candidates.includes(productId);
    }

    private isParcelaOptionForSelectedFinca(option: unknown, finca: unknown): boolean {
        const fincaId = TratamientosControlValueResolver.resolveOptionId(finca);

        if (!fincaId) {
            return false;
        }

        const optionRecord = option !== null && typeof option === 'object'
            ? option as Record<string, unknown>
            : {};
        const value = optionRecord['value'];
        const raw = optionRecord['raw'];
        const parcela = this.resolveParcelaRecord(optionRecord, value, raw);
        const parcelaFincaId = TratamientosControlValueResolver.resolveOptionId(parcela['id_finca']);

        return parcelaFincaId === fincaId;
    }

    private resolveParcelaRecord(
        optionRecord: Record<string, unknown>,
        value: unknown,
        raw: unknown,
    ): Record<string, unknown> {
        if (raw !== null && typeof raw === 'object') {
            return raw as Record<string, unknown>;
        }

        if (value !== null && typeof value === 'object') {
            return value as Record<string, unknown>;
        }

        return optionRecord;
    }

    private buildForm(): Form<TratamientosControlModel> {
        const action = this.formType === FormType.CREATION ? 'Crear' : 'Actualizar';

        return {
            name: 'tratamientos-control',
            header: `${action} Control de Tratamientos`,
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: { isEnabled: false, label: '' },
            signed: false,
        };
    }

    private getFormSections(): FormSection<any>[] {
        return [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getMainFields(),
                order: 1
            },
            {
                name: 'Materias activas',
                width: 12,
                isVisible: true,
                fields: this.getDosisFields(),
                order: 2
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 3
            }
        ];
    }

    private getMainFields(): FormField<any>[] {
        return [
            {
                name: 'id_finca',
                label: this.fincaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.fincas,
                    dropdownConfiguration: { valuePrimaryKey: 'id' }
                },
                width: 3,
                conditions: { isRequired: true, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: true },
                order: 1,
                vinculatedFields: undefined
            },
            {
                name: 'ids_parcelas',
                label: this.parcelasName,
                input: {
                    type: InputType.MULTISELECT,
                    isEditable: true,
                    data: this.lists.parcelas,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (option, context) => {
                            const finca = context?.model?.get('id_finca')?.value;
                            const idFinca = TratamientosControlValueResolver.resolveOptionId(finca);

                            return !idFinca || option.value?.id_finca?.toString() === idFinca;
                        }
                    }
                },
                dependencyBehaviour: {
                    clearOnParentChange: true,
                    filterLocally: true,
                    keepValueUntilOptionsLoaded: true,
                    clearOnHydration: false,
                },
                width: 3,
                conditions: {
                    isRequired: false,
                    isVisible: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isRememberedOnDuplicate: false
                },
                order: 2,
                dependencies: [
                    {
                        source: 'id_finca',
                        target: 'ids_parcelas',
                        kind: 'filter'
                    }
                ],
                vinculatedFields: undefined
            },
            {
                name: 'fecha',
                label: 'Fecha',
                input: { type: InputType.CALENDAR, isEditable: true },
                width: 3,
                conditions: { isRequired: true, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: false },
                order: 3,
                vinculatedFields: undefined
            },
            {
                name: 'estado',
                label: 'Estado',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: {
                        filtered: [
                            { label: 'Planificado', value: 'Planificado' },
                            { label: 'Orden ficha', value: 'Orden ficha' },
                            { label: 'Aplicado', value: 'Aplicado' }
                        ],
                        values: []
                    },
                    defaultValue: 'Planificado'
                },
                width: 3,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: true },
                order: 4,
                vinculatedFields: undefined
            },
            {
                name: 'plagas',
                label: 'Nombre',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: { isRequired: true, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: true },
                order: 5,
                vinculatedFields: undefined
            },
            {
                name: 'litros',
                label: 'Litros de caldo',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: {
                    isRequired: false,
                    isVisible: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isRememberedOnDuplicate: false
                },
                order: 6,
                vinculatedFields: undefined
            },
            {
                name: 'ph_caldo',
                label: 'pH Caldo',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: {
                    isRequired: false,
                    isVisible: this.appName === 'hernandorena',
                    isRetained: false,
                    hasUndoEnabled: false,
                    isRememberedOnDuplicate: false
                },
                order: 7,
                vinculatedFields: undefined
            },
            {
                name: 'ph_agua',
                label: 'pH Agua',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: {
                    isRequired: false,
                    isVisible: this.appName === 'hernandorena',
                    isRetained: false,
                    hasUndoEnabled: false,
                    isRememberedOnDuplicate: false
                },
                order: 8,
                vinculatedFields: undefined
            },
            {
                name: 'ph_final',
                label: 'pH Final',
                input: { type: InputType.NUMERIC, isEditable: true },
                width: 3,
                conditions: {
                    isRequired: false,
                    isVisible: this.appName === 'hernandorena',
                    isRetained: false,
                    hasUndoEnabled: false,
                    isRememberedOnDuplicate: false
                },
                order: 9,
                vinculatedFields: undefined
            },
            {
                name: 'id_maquinaria',
                label: 'Maquinaria',
                input: {
                    type: InputType.DROPDOWN,
                    isEditable: true,
                    data: this.lists.maquinarias,
                    dropdownConfiguration: { valuePrimaryKey: 'id' }
                },
                width: 3,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: true },
                order: 10,
                vinculatedFields: undefined
            },
            {
                name: 'observaciones',
                label: 'Observaciones',
                input: { type: InputType.TEXTAREA, isEditable: true },
                width: 6,
                conditions: {
                    isRequired: false,
                    isVisible: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isRememberedOnDuplicate: false
                },
                order: 11,
                vinculatedFields: undefined
            },
            {
                name: 'epi',
                label: 'EPI',
                input: { type: InputType.TEXT, isEditable: true },
                width: 3,
                conditions: {
                    isRequired: false,
                    isVisible: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isRememberedOnDuplicate: false
                },
                order: 12,
                vinculatedFields: undefined
            }
        ];
    }


    private getDosisFields(): FormField<any>[] {
        return [
            {
                name: 'lineas_materias',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.REPEATABLE_GROUP,
                    isEditable: true,
                    repeatableGroupConfiguration: {
                        rows: 3,
                        addButtonLabel: 'Añadir materia activa',
                        bindings: {
                            producto:       '_linea_producto',
                            uso:            '_linea_uso',
                            plaga:          '_linea_plaga',
                            dosisCuaderno:  '_linea_dosis_cuaderno',
                            udCuaderno:     '_linea_ud_cuaderno',
                            dosis:          '_linea_dosis',
                            udReal:         '_linea_ud_real',
                        },
                        serialization: { separator: ';' },
                        domainAdapter: {
                            getRowExtraView: (ctx) => this.dropdownDataSourceFactory.getRowExtraView(ctx),
                        },
                        legacySlotSerialization: {
                            rows: 3,
                            removeBindingFields: true,
                            targets: {
                                producto: { fieldName: 'id_producto{index}', emptyValue: null },
                                uso: { fieldName: 'uso{index}', emptyValue: '' },
                                plaga: { fieldName: 'plaga{index}', emptyValue: '' },
                                dosisCuaderno: { fieldName: 'dosis_cuaderno{index}', emptyValue: '' },
                                udCuaderno: { fieldName: 'ud_cuaderno_{index}', emptyValue: '' },
                                dosis: { fieldName: 'dosis{index}', emptyValue: '' },
                                udReal: { fieldName: 'ud_real_{index}', emptyValue: '' },
                            },
                        },
                        subFields: this.getDosisSubFields()
                    }
                },
                width: 12,
                conditions: {
                    isRememberedOnDuplicate: false,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 1
            }
        ];
    }

    private getDosisSubFields(): any[] {
        return [
            {
                key: 'producto',
                label: 'Materia activa',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.lists.productos,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        controlValueKey: 'id',
                        backendValueKey: 'id',
                        rawIdentityKeys: ['id', 'id_producto', 'producto.id', 'producto.id_producto']
                    }
                }
            },
            {
                key: 'uso',
                label: 'Uso',
                dependsOn: ['producto'],
                dependencyBehaviour: { clearOnParentChange: true, autoSelect: 'singleOption' },
                loadData: this.dropdownDataSourceFactory.getLoadData('uso'),
                getData: (ctx: RepeatableSubFieldDependencyContext) => this.dropdownDataSourceFactory.getData('uso', ctx.rowIndex ?? 0),
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.dropdownDataSourceFactory.getData('uso', 0)
                }
            },
            {
                key: 'plaga',
                label: 'Plaga',
                dependsOn: ['producto', 'uso'],
                dependencyBehaviour: { clearOnParentChange: true, autoSelect: 'singleOption' },
                loadData: this.dropdownDataSourceFactory.getLoadData('plaga'),
                getData: (ctx: RepeatableSubFieldDependencyContext) => this.dropdownDataSourceFactory.getData('plaga', ctx.rowIndex ?? 0),
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    isEditable: true,
                    data: this.dropdownDataSourceFactory.getData('plaga', 0)
                }
            },
            {
                key: 'dosisCuaderno',
                label: 'Dosis cuaderno',
                input: { type: InputType.NUMERIC, isEditable: true }
            },
            {
                key: 'udCuaderno',
                label: 'Ud. Medida Cuaderno',
                dependsOn: ['plaga'],
                dependencyBehaviour: { clearOnParentChange: false },
                onDependencyChange: (ctx: RepeatableSubFieldDependencyContext) =>
                    this.dropdownDataSourceFactory.patchUnitsAfterPlagaChange(ctx),
                input: { type: InputType.TEXT, isEditable: true }
            },
            {
                key: 'dosis',
                label: 'Dosis aplicada',
                input: { type: InputType.NUMERIC, isEditable: true }
            },
            {
                key: 'udReal',
                label: 'Ud. Medida Aplicada',
                dependsOn: ['plaga'],
                dependencyBehaviour: { clearOnParentChange: false },
                onDependencyChange: (ctx: RepeatableSubFieldDependencyContext) =>
                    this.dropdownDataSourceFactory.patchUnitsAfterPlagaChange(ctx),
                input: { type: InputType.TEXT, isEditable: true }
            }
        ];
    }

    private getHiddenFields(): FormField<any>[] {
        return [
            {
                name: 'id',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: {
                    isRequired: false,
                    isVisible: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isRememberedOnDuplicate: false
                },
                order: 1,
                vinculatedFields: undefined
            },
            {
                name: 'id_cliente',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false, defaultValue: this.cliente?.id ?? undefined },
                updateValue: () => this.cliente?.id ?? undefined,
                width: 2,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: true },
                order: 2,
                vinculatedFields: undefined
            },
            {
                name: 'id_usuario',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false, defaultValue: this.usuario?.id ?? undefined },
                updateValue: () => this.usuario?.id ?? undefined,
                width: 2,
                conditions: { isRequired: false, isVisible: true, isRetained: false, hasUndoEnabled: false, isRememberedOnDuplicate: true },
                order: 3,
                vinculatedFields: undefined
            },
            {
                name: 'id_tratamiento',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: {
                    isRequired: false,
                    isVisible: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isRememberedOnDuplicate: false
                },
                order: 4,
                vinculatedFields: undefined
            },
            {
                name: 'id_tarea',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: {
                    isRequired: false,
                    isVisible: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isRememberedOnDuplicate: false
                },
                order: 5,
                vinculatedFields: undefined
            },
            {
                name: 'cultivo',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: {
                    isRequired: false,
                    isVisible: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isRememberedOnDuplicate: false
                },
                order: 6,
                vinculatedFields: undefined
            },
            {
                name: 'variedad',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: {
                    isRequired: false,
                    isVisible: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isRememberedOnDuplicate: false
                },
                order: 7,
                vinculatedFields: undefined
            },
            {
                name: 'sup_cultivada',
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 2,
                conditions: {
                    isRequired: false,
                    isVisible: true,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isRememberedOnDuplicate: false
                },
                order: 8,
                vinculatedFields: undefined
            },
            ...([
                'id_producto1', 'id_producto2', 'id_producto3',
                'uso1', 'uso2', 'uso3',
                'plaga1', 'plaga2', 'plaga3',
                'dosis_cuaderno1', 'dosis_cuaderno2', 'dosis_cuaderno3',
                'ud_cuaderno_1', 'ud_cuaderno_2', 'ud_cuaderno_3',
                'dosis1', 'dosis2', 'dosis3',
                'ud_real_1', 'ud_real_2', 'ud_real_3',
            ] as const).map((name, idx) => ({
                name,
                vinculatedFields: undefined,
                label: '',
                input: { type: InputType.HIDDEN, isEditable: true },
                width: 1,
                conditions: {
                    isRememberedOnDuplicate: false,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 100 + idx
            })),
            ...([
                '_linea_producto', '_linea_uso', '_linea_plaga',
                '_linea_dosis_cuaderno', '_linea_ud_cuaderno',
                '_linea_dosis', '_linea_ud_real'
            ] as const).map((name, idx) => ({
                name,
                vinculatedFields: undefined,
                label: '',
                input: { type: InputType.HIDDEN, isEditable: false },
                width: 1,
                conditions: {
                    isRememberedOnDuplicate: false,
                    isRequired: false,
                    isRetained: false,
                    hasUndoEnabled: false,
                    isVisible: true
                },
                order: 200 + idx
            }))
        ];
    }
}
